import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Define your base URL
const baseUrl = process.env.REACT_APP_API_URL || "https://api.naturalremediesapp.com/api";

// Function to create request
const createRequest = (url) => ({ url });

// Function to check if token is expired
const isTokenExpired = (tokenExpiry) => {
  if (!tokenExpiry) return true;
  return new Date(tokenExpiry) <= new Date();
};

// Function to refresh token
const refreshToken = async () => {
  const refreshToken = localStorage.getItem("admin_refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await fetch(`${baseUrl}/auth/admin/refresh?refresh_token=${refreshToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();
    
    if (data.success && data.access_token) {
      localStorage.setItem("admin_token", data.access_token);
      localStorage.setItem("admin_refresh_token", data.refresh_token);
      localStorage.setItem("admin_token_expires_at", data.access_token_expires_at);
      localStorage.setItem("admin_refresh_token_expires_at", data.refresh_token_expires_at);
      return data.access_token;
    } else {
      throw new Error("Failed to refresh token");
    }
  } catch (error) {
    // Clear all tokens on refresh failure
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_token_expires_at");
    localStorage.removeItem("admin_refresh_token_expires_at");
    localStorage.removeItem("admin_data");
    throw error;
  }
};

// Create the API slice using RTK Query
export const bodySystemsApiService = createApi({
  reducerPath: "bodySystemsApiService",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      // Get the token from localStorage
      const token = localStorage.getItem("admin_token");
      const tokenExpiry = localStorage.getItem("admin_token_expires_at");

      // Check if token is expired and try to refresh it
      if (token && isTokenExpired(tokenExpiry)) {
        try {
          const newToken = await refreshToken();
          headers.set("Authorization", `Bearer ${newToken}`);
        } catch (error) {
          // If refresh fails, redirect to login
          window.location.href = "/admin/auth/sign-in";
          throw error;
        }
      } else if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  }),

  endpoints: (builder) => ({
    // Get all body systems with search and pagination
    getBodySystems: builder.query({
      query: (searchParams = {}) => {
        const params = new URLSearchParams();
        if (searchParams.title) {
          params.append('title', searchParams.title);
        }
        if (searchParams.page) {
          params.append('page', String(searchParams.page));
        }
        if (searchParams.per_page) {
          params.append('per_page', String(searchParams.per_page));
        }
        const queryString = params.toString();
        return {
          url: `/body-systems${queryString ? `?${queryString}` : ''}`,
        };
      },
      transformResponse: (response) => {
        // Return the full response to access both data and pagination
        return response;
      },
    }),

    // Get body system by ID
    getBodySystem: builder.query({
      query: (id) => createRequest(`/body-systems/${id}`),
    }),

    // Create body system
    createBodySystem: builder.mutation({
      query: (bodySystem) => ({
        url: "/body-systems",
        method: "POST",
        body: bodySystem,
      }),
    }),

    // Update body system
    updateBodySystem: builder.mutation({
      query: ({id, bodySystem}) => ({
        url: `/body-systems/${id}`,
        method: "PUT",
        body: bodySystem,
      }),
    }),

    // Delete body system
    deleteBodySystem: builder.mutation({
      query: (id) => ({
        url: `/body-systems/${id}`,
        method: "DELETE",
      }),
    }),
  }),
});

// Export hooks generated by the API service
export const { 
  useGetBodySystemsQuery, 
  useGetBodySystemQuery, 
  useCreateBodySystemMutation, 
  useUpdateBodySystemMutation, 
  useDeleteBodySystemMutation
} = bodySystemsApiService;

// Export utility functions
export { refreshToken, isTokenExpired }; 